<template>
  <div class="register-container">
    <div class="register-box">
      <h1 class="register-title">{{ $t('register.title') }}</h1>

      <el-form
          ref="registerForm"
          :model="registerForm"
          :rules="registerRules"
          class="register-form"
          @keyup.enter.native="handleRegister"
      >
        <!-- 根据语言显示：中文显示手机号，英文显示邮箱 -->
        <el-form-item v-if="isChinese" prop="phoneNumber">
          <el-input
              v-model="registerForm.phoneNumber"
              :placeholder="$t('register.phonePlaceholder')"
              prefix-icon="el-icon-phone"
              maxlength="11"
              @input="formatPhone"
          ></el-input>
        </el-form-item>
        <el-form-item v-else prop="email">
          <el-input
              v-model="registerForm.email"
              :placeholder="$t('register.emailPlaceholder')"
              prefix-icon="el-icon-message"
              @input="formatEmail"
          ></el-input>
        </el-form-item>

        <!-- 验证码输入框 + 获取验证码按钮（同一行） -->
        <el-form-item prop="code">
          <el-row :gutter="10">
            <el-col :span="16">
              <el-input
                  v-model="registerForm.code"
                  :placeholder="$t('register.codePlaceholder')"
                  prefix-icon="el-icon-key"
                  maxlength="6"
                  @input="formatCode"
              ></el-input>
            </el-col>
            <el-col :span="8">
              <el-button
                  type="primary"
                  class="code-btn"
                  :disabled="countdown > 0 || !isInputValid"
                  @click="getVerificationCode"
              >
                {{ countdown > 0 ? `${countdown}${$t('register.resendAfter')}` : $t('register.getCode') }}
              </el-button>
            </el-col>
          </el-row>
        </el-form-item>

        <!-- 密码输入框 -->
        <el-form-item prop="password">
          <el-input
              v-model="registerForm.password"
              type="password"
              :placeholder="$t('register.passwordPlaceholder')"
              prefix-icon="el-icon-lock"
              show-password
          ></el-input>
        </el-form-item>

        <!-- 确认密码输入框 -->
        <el-form-item prop="confirmPassword">
          <el-input
              v-model="registerForm.confirmPassword"
              type="password"
              :placeholder="$t('register.confirmPasswordPlaceholder')"
              prefix-icon="el-icon-lock"
              show-password
          ></el-input>
        </el-form-item>

        <!-- 注册按钮 -->
        <el-form-item>
          <el-button
              type="primary"
              class="register-btn"
              :loading="loading"
              @click="handleRegister"
          >
            {{ $t('register.register') }}
          </el-button>
        </el-form-item>
      </el-form>

      <!-- 登录跳转 -->
      <div class="register-footer">
        <span @click="goToLogin">{{ $t('register.hasAccount') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RegisterView',
  data() {
    // 密码一致性验证
    const validatePassword = (rule, value, callback) => {
      if (value !== this.registerForm.password) {
        callback(new Error(this.$t('register.passwordMismatch')))
      } else {
        callback()
      }
    }

    return {
      registerForm: {
        phoneNumber: '',  // 手机号（中文时使用）
        email: '',        // 邮箱（英文时使用）
        code: '',
        password: '',
        confirmPassword: ''
      },
      loading: false,   // 注册按钮加载状态
      countdown: 0,     // 倒计时秒数
      timer: null       // 倒计时定时器
    }
  },
  computed: {
    // 判断是否为中文
    isChinese() {
      return this.$store.state.locale === 'zh'
    },
    // 手机号格式验证（控制获取验证码按钮状态）
    isPhoneValid() {
      const phoneReg = /^1[3-9]\d{9}$/
      return phoneReg.test(this.registerForm.phoneNumber)
    },
    // 邮箱格式验证（控制获取验证码按钮状态）
    isEmailValid() {
      const emailReg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailReg.test(this.registerForm.email)
    },
    // 根据语言返回对应的验证状态
    isInputValid() {
      return this.isChinese ? this.isPhoneValid : this.isEmailValid
    },
    // 动态验证规则
    registerRules() {
      const validatePassword = (rule, value, callback) => {
        if (value !== this.registerForm.password) {
          callback(new Error(this.$t('register.passwordMismatch')))
        } else {
          callback()
        }
      }

      const rules = {
        // 验证码验证
        code: [
          {required: true, message: this.$t('register.codeRequired'), trigger: 'blur'},
          {pattern: /^\d{6}$/, message: this.$t('register.codeFormatError'), trigger: 'blur'}
        ],
        // 密码验证
        password: [
          {required: true, message: this.$t('register.passwordRequired'), trigger: 'blur'},
          {min: 6, max: 20, message: this.$t('register.passwordLengthError'), trigger: 'blur'}
        ],
        // 确认密码验证
        confirmPassword: [
          {required: true, message: this.$t('register.confirmPasswordRequired'), trigger: 'blur'},
          {validator: validatePassword, trigger: 'blur'}
        ]
      }

      // 根据语言添加不同的验证规则
      if (this.isChinese) {
        // 中文：手机号验证
        rules.phoneNumber = [
          {required: true, message: this.$t('register.phoneRequired'), trigger: 'blur'},
          {pattern: /^1[3-9]\d{9}$/, message: this.$t('register.phoneFormatError'), trigger: 'blur'}
        ]
      } else {
        // 英文：邮箱验证
        rules.email = [
          {required: true, message: this.$t('register.emailRequired'), trigger: 'blur'},
          {type: 'email', message: this.$t('register.emailFormatError'), trigger: 'blur'}
        ]
      }

      return rules
    }
  },
  beforeDestroy() {
    // 组件销毁前清除定时器
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  methods: {
    // 手机号格式化（仅允许数字）
    formatPhone() {
      this.registerForm.phoneNumber = this.registerForm.phoneNumber.replace(/\D/g, '')
    },

    // 邮箱格式化（移除空格）
    formatEmail() {
      this.registerForm.email = this.registerForm.email.replace(/\s/g, '')
    },

    // 验证码格式化（仅允许数字）
    formatCode() {
      this.registerForm.code = this.registerForm.code.replace(/\D/g, '')
    },

    // 获取注册验证码
    getVerificationCode() {
      const fieldName = this.isChinese ? 'phoneNumber' : 'email'
      // 先验证输入格式
      this.$refs.registerForm.validateField(fieldName, (error) => {
        if (!error) {
          // 调用获取验证码接口
          const params = this.isChinese 
            ? { phone: this.registerForm.phoneNumber }
            : { email: this.registerForm.email }
          this.$api.auth.getRegisterCode(params).then(res => {
            if (res.statusCode === 100200) {
              this.$message.success(this.$t('register.codeSent'))
              // 启动60秒倒计时
              this.startCountdown()
            } else {
              this.$message.error(this.$t('register.getCodeError'))
            }
          }).catch(err => {
            console.error('获取验证码出错:', err)
            // 如果是业务错误，使用国际化提示；如果是网络错误，使用网络错误提示
            if (err.isBusinessError) {
              this.$message.error(this.$t('register.getCodeError'))
            } else {
              this.$message.error(this.$t('register.networkError'))
            }
          })
        }
      })
    },

    // 倒计时逻辑
    startCountdown() {
      this.countdown = 60
      this.timer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--
        } else {
          clearInterval(this.timer)
          this.timer = null
        }
      }, 1000)
    },

    // 处理注册
    handleRegister() {
      this.$refs.registerForm.validate(valid => {
        if (valid) {
          this.loading = true
          // 根据语言构建注册参数
          const registerParams = this.isChinese
            ? {
                phoneNumber: this.registerForm.phoneNumber,
                code: this.registerForm.code,
                password: this.registerForm.password
              }
            : {
                email: this.registerForm.email,
                code: this.registerForm.code,
                password: this.registerForm.password
              }
          // 调用注册接口
          this.$api.auth.register(registerParams).then(res => {
            if (res.statusCode === 100200) {
              this.$message.success(this.$t('register.registerSuccess'))
              setTimeout(() => {
                this.$router.push('/login')
              }, 1500)
            } else {
              this.$message.error(this.$t('register.registerError'))
            }
          }).catch(err => {
            console.error('注册出错:', err)
            // 如果是业务错误，使用国际化提示；如果是网络错误，使用网络错误提示
            if (err.isBusinessError) {
              this.$message.error(this.$t('register.registerError'))
            } else {
              this.$message.error(this.$t('register.networkError'))
            }
          }).finally(() => {
            this.loading = false
          })
        } else {
          return false
        }
      })
    },

    // 跳转到登录页
    goToLogin() {
      this.$router.push('/login')
    }
  }
}
</script>

<style scoped>
.register-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f0f2f5;
  background-image: url("@/assets/index/bc.jpg");
  background-size: cover;
  background-position: center;
}

.register-box {
  width: 90%;
  max-width: 450px;
  padding: 30px;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
}

.register-title {
  margin-bottom: 30px;
  text-align: center;
  color: #333;
  font-size: 24px;
}

.register-form {
  margin-bottom: 20px;
}

/* 注册按钮和验证码按钮全屏宽 */
.register-btn, .code-btn {
  width: 100%;
}

.register-footer {
  display: flex;
  justify-content: center;
  font-size: 14px;
  color: #666;
}

.register-footer span {
  cursor: pointer;
  transition: color 0.3s;
}

.register-footer span:hover {
  color: #409EFF;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .register-box {
    width: 85%;
    padding: 20px;
  }

  .register-title {
    font-size: 20px;
    margin-bottom: 20px;
  }
}

@media (max-width: 480px) {
  .register-box {
    width: 90%;
    padding: 15px;
  }

  .register-title {
    font-size: 18px;
  }
}
</style>
